import { useCallback, useEffect, useMemo, useState } from 'react';
import type { LocationCredential } from '@/app/session/startup-session';
import {
  type LocationInventoryStats,
  LocationInventoryStatsClient,
} from '@/features/today/location-inventory-stats-client';

interface LocationInventoryStatsState {
  error: string | null;
  isLoading: boolean;
  stats: LocationInventoryStats | null;
}

export function useLocationInventoryStats(activeLocation: LocationCredential | null) {
  const client = useMemo(() => new LocationInventoryStatsClient(), []);
  const [state, setState] = useState<LocationInventoryStatsState>({
    error: null,
    isLoading: false,
    stats: null,
  });

  const refresh = useCallback(
    async (input: { refreshCache?: boolean } = {}) => {
      if (!activeLocation) {
        setState({ error: null, isLoading: false, stats: null });
        return;
      }

      if (!activeLocation.locationId) {
        setState({
          error: 'Active location is missing a backend location id. Revalidate the location API key.',
          isLoading: false,
          stats: null,
        });
        return;
      }

      setState(current => ({ ...current, error: null, isLoading: true }));

      try {
        const stats = await client.getStats({
          locationApiKey: activeLocation.apiKey,
          locationId: activeLocation.locationId,
          refreshCache: input.refreshCache,
        });
        setState({ error: null, isLoading: false, stats });
      } catch (error) {
        setState({
          error: error instanceof Error ? error.message : 'Unable to load location stats.',
          isLoading: false,
          stats: null,
        });
      }
    },
    [activeLocation, client],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    ...state,
    refresh,
  };
}
